import { trackEvent, trackPage } from 'utils/analytics'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useContext } from 'react'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import logger from '@achieve/cx-logger'

const useTriggerEvents = () => {
  const analyticsContext = useContext(AnalyticsContext)

  // Will reset session start time and create new session if last event was >30 min prior
  const { query } = useAppRouter()

  const resolveAnalyticsState = () => {
    const contextState = analyticsContext?.state || {}
    const globalState = globalThis.__acxAnalyticsState || {}
    return contextState?.analyticsInstance ? contextState : globalState
  }

  const isPznEvent = () => query?.pzn === 'on' || globalThis.__acxPznEligible === true

  const fireAndForget = (event) => {
    const {
      analyticsInstance,
      trackingData,
      isPageLoaded,
      auth0_email,
      auth0_user_id,
      profile_id,
      config,
      quiz_name,
      quiz_page,
      quiz_score,
    } = resolveAnalyticsState()

    if (!isPageLoaded) return
    trackEvent(analyticsInstance, config, {
      ...trackingData,
      auth0_email,
      auth0_user_id,
      profile_id,
      ...event,
      ...(quiz_name && {
        quiz_name: quiz_name,
      }),
      ...(quiz_page && {
        quiz_page: event?.quiz_page || quiz_page,
      }),
      ...(quiz_score && {
        quiz_score: quiz_score,
      }),
      event_pzn: isPznEvent(),
    })
  }

  const fireAndForgetPage = (event) => {
    const {
      analyticsInstance,
      trackingData,
      isPageLoaded,
      auth0_email,
      auth0_user_id,
      profile_id,
      config,
      quiz_name,
      quiz_page,
      quiz_score,
    } = resolveAnalyticsState()

    if (!isPageLoaded) return
    trackPage(analyticsInstance, config, {
      ...trackingData,
      auth0_email,
      auth0_user_id,
      profile_id,
      ...event,
      ...(quiz_name && {
        quiz_name: quiz_name,
      }),
      ...(quiz_page && {
        quiz_page: event?.quiz_page || quiz_page,
      }),
      ...(quiz_score && {
        quiz_score: quiz_score,
      }),
      event_pzn: isPznEvent(),
    })
  }

  // Resolves once the event is tracked or after timeoutMs, whichever happens first
  const waitForNavigation = async (event, timeoutMs = 1000) => {
    const {
      analyticsInstance,
      trackingData,
      isPageLoaded,
      auth0_email,
      auth0_user_id,
      profile_id,
      config,
      quiz_name,
      quiz_page,
      quiz_score,
    } = resolveAnalyticsState()

    if (!isPageLoaded) return
    let trackingDone = false
    const trackPromise = trackEvent(
      analyticsInstance,
      config,
      {
        ...trackingData,
        auth0_email,
        auth0_user_id,
        profile_id,
        ...event,
        ...(quiz_name && {
          quiz_name: quiz_name,
        }),
        ...(quiz_page && {
          quiz_page: quiz_page,
        }),
        ...(quiz_score && {
          quiz_score: quiz_score,
        }),
        event_pzn: isPznEvent(),
      },
      true
    ).finally(() => {
      trackingDone = true
    })

    return Promise.race([
      new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!trackingDone) {
            logger.log('Event timed out.')
            logger.log(event)
            reject()
          }
          resolve()
        }, timeoutMs)
      }).catch(() => {}),
      trackPromise,
    ])
  }

  return { fireAndForget, waitForNavigation, fireAndForgetPage }
}
export { useTriggerEvents }
